import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useI18n } from '@/lib/i18n'
import {
  FACTION_EMOJIS,
  ROLE_EMOJIS,
  type PlayerFactionType,
  type PlayerRoleType,
} from '@repo/sdk'
import { Pencil } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

export interface ReplaceRoleHypothesesInput {
  viewerSeatId: string
  targetSeatId: string
  factions: RoleHypothesisFaction[]
  roles: PlayerRoleType[]
  expectedRevision: number
}

export type RoleHypothesisFaction = PlayerFactionType

export type ReplaceRoleHypothesesResult
  = | {
    status: 'success'
    targetSeatId: string
    selectedFactions: RoleHypothesisFaction[]
    selectedRoles: PlayerRoleType[]
    revision: number
  }
  | {
    status: 'conflict'
    reason: 'stale_revision' | 'role_known' | 'game_closed'
    refetch: true
  }

interface RoleHypothesisPickerProps {
  viewerSeatId: string
  targetSeatId: string
  targetPosition: number
  targetName: string
  selectedFactions: RoleHypothesisFaction[]
  selectedRoles: PlayerRoleType[]
  revision: number
  candidates: {
    good: PlayerRoleType[]
    evil: PlayerRoleType[]
  }
  onReplace: (
    input: ReplaceRoleHypothesesInput,
  ) => Promise<ReplaceRoleHypothesesResult>
}

type SaveState = 'idle' | 'saving' | 'saved' | 'conflict' | 'failed'

interface HypothesisSelection {
  factions: RoleHypothesisFaction[]
  roles: PlayerRoleType[]
}

const FACTION_ORDER: readonly RoleHypothesisFaction[] = ['good', 'evil']
const FACTION_LABEL_KEYS: Record<RoleHypothesisFaction, string> = {
  good: 'game.goodFaction',
  evil: 'game.evilFaction',
}

function sameSelection(
  left: HypothesisSelection,
  right: HypothesisSelection,
): boolean {
  return (
    left.factions.length === right.factions.length
    && left.factions.every(
      (faction, index) => faction === right.factions[index],
    )
    && left.roles.length === right.roles.length
    && left.roles.every((role, index) => role === right.roles[index])
  )
}

export function RoleHypothesisPicker({
  targetPosition,
  targetName,
  selectedFactions,
  selectedRoles,
  revision,
  candidates,
  viewerSeatId,
  targetSeatId,
  onReplace,
}: RoleHypothesisPickerProps) {
  const { t } = useI18n()
  const roleOrder = useMemo(
    () => [...candidates.good, ...candidates.evil],
    [candidates.evil, candidates.good],
  )
  const goodRoleSet = useMemo(
    () => new Set(candidates.good),
    [candidates.good],
  )
  const rolesByFaction = useMemo(
    () => ({ good: candidates.good, evil: candidates.evil }),
    [candidates.evil, candidates.good],
  )
  const factionForRole = useCallback(
    (role: PlayerRoleType): RoleHypothesisFaction =>
      goodRoleSet.has(role) ? 'good' : 'evil',
    [goodRoleSet],
  )
  const normalizeRoles = useCallback(
    (roles: readonly PlayerRoleType[]) => {
      const selected = new Set(roles)
      return roleOrder.filter(role => selected.has(role))
    },
    [roleOrder],
  )
  const normalizeSelection = useCallback(
    (selection: HypothesisSelection): HypothesisSelection => {
      const selectedFactionSet = new Set(selection.factions)
      const factions = FACTION_ORDER.filter(faction =>
        selectedFactionSet.has(faction),
      )
      return {
        factions,
        roles: normalizeRoles(selection.roles).filter(role =>
          !factions.includes(factionForRole(role)),
        ),
      }
    },
    [factionForRole, normalizeRoles],
  )
  const initialSelection = normalizeSelection({
    factions: selectedFactions,
    roles: selectedRoles,
  })
  const [desiredSelection, setDesiredSelection] = useState(initialSelection)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const stableRef = useRef({ selection: initialSelection, revision })
  const desiredRef = useRef(initialSelection)
  const pendingRef = useRef(false)
  const mountedRef = useRef(true)
  const replaceRef = useRef(onReplace)
  const pumpRef = useRef<() => void>(() => {})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const descriptionId = useId()
  const moreDescriptionId = useId()
  const statusId = useId()

  useEffect(() => {
    replaceRef.current = onReplace
  }, [onReplace])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const incoming = normalizeSelection({
      factions: selectedFactions,
      roles: selectedRoles,
    })
    const stable = stableRef.current
    if (revision < stable.revision)
      return

    const hasLocalChange = !sameSelection(
      desiredRef.current,
      stable.selection,
    )
    stableRef.current = { selection: incoming, revision }
    if (!hasLocalChange) {
      desiredRef.current = incoming
      setDesiredSelection(incoming)
    }
  }, [normalizeSelection, revision, selectedFactions, selectedRoles])

  const pump = useCallback(() => {
    if (pendingRef.current)
      return
    const requestedSelection = desiredRef.current
    const stable = stableRef.current
    if (sameSelection(requestedSelection, stable.selection))
      return

    pendingRef.current = true
    setSaveState('saving')
    void replaceRef
      .current({
        viewerSeatId,
        targetSeatId,
        factions: requestedSelection.factions,
        roles: requestedSelection.roles,
        expectedRevision: stable.revision,
      })
      .then((result) => {
        if (!mountedRef.current)
          return
        if (result.status === 'conflict') {
          desiredRef.current = stableRef.current.selection
          setDesiredSelection(stableRef.current.selection)
          setSaveState('conflict')
          return
        }

        const normalizedResult = normalizeSelection({
          factions: result.selectedFactions,
          roles: result.selectedRoles,
        })
        if (result.revision < stableRef.current.revision) {
          if (sameSelection(desiredRef.current, requestedSelection)) {
            desiredRef.current = stableRef.current.selection
            setDesiredSelection(stableRef.current.selection)
          }
          setSaveState('saved')
          return
        }
        stableRef.current = {
          selection: normalizedResult,
          revision: result.revision,
        }
        if (sameSelection(desiredRef.current, requestedSelection)) {
          desiredRef.current = normalizedResult
          setDesiredSelection(normalizedResult)
        }
        setSaveState('saved')
      })
      .catch(() => {
        if (!mountedRef.current)
          return
        desiredRef.current = stableRef.current.selection
        setDesiredSelection(stableRef.current.selection)
        setSaveState('failed')
      })
      .finally(() => {
        pendingRef.current = false
        if (!mountedRef.current)
          return
        queueMicrotask(() => pumpRef.current())
      })
  }, [normalizeSelection, targetSeatId, viewerSeatId])

  useEffect(() => {
    pumpRef.current = pump
  }, [pump])

  const replaceDesiredSelection = (selection: HypothesisSelection) => {
    const normalizedSelection = normalizeSelection(selection)
    desiredRef.current = normalizedSelection
    setDesiredSelection(normalizedSelection)
    queueMicrotask(() => pumpRef.current())
  }

  const toggleRole = (role: PlayerRoleType, checked: boolean) => {
    const faction = factionForRole(role)
    replaceDesiredSelection({
      factions: checked
        ? desiredRef.current.factions.filter(selected => selected !== faction)
        : desiredRef.current.factions,
      roles: checked
        ? [...desiredRef.current.roles, role]
        : desiredRef.current.roles.filter(selected => selected !== role),
    })
  }

  const toggleFaction = (
    faction: RoleHypothesisFaction,
    checked: boolean,
  ) => {
    const factionRoles = rolesByFaction[faction]
    replaceDesiredSelection({
      factions: checked
        ? [...desiredRef.current.factions, faction]
        : desiredRef.current.factions.filter(selected => selected !== faction),
      roles: checked
        ? desiredRef.current.roles.filter(role => !factionRoles.includes(role))
        : desiredRef.current.roles,
    })
  }

  const selectedGuesses = FACTION_ORDER.flatMap((faction) => {
    if (desiredSelection.factions.includes(faction)) {
      return [{
        key: `faction:${faction}`,
        emoji: FACTION_EMOJIS[faction],
        name: t(FACTION_LABEL_KEYS[faction]),
      }]
    }
    const factionRoles = rolesByFaction[faction]
    return factionRoles
      .filter(role => desiredSelection.roles.includes(role))
      .map(role => ({
        key: `role:${role}`,
        emoji: ROLE_EMOJIS[role],
        name: t(`game.${role}`),
      }))
  })
  const displayedGuesses = selectedGuesses.slice(0, 2)
  const hiddenGuessCount = Math.max(
    0,
    selectedGuesses.length - displayedGuesses.length,
  )
  const statusText
    = saveState === 'idle' ? '' : t(`play.roleHypotheses.${saveState}`)
  const selectedGuessNames = selectedGuesses
    .map(guess => guess.name)
    .join(', ')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="ghost"
          size="sm"
          className="relative z-20 h-auto min-h-6 max-w-full flex-wrap justify-start gap-1 rounded-md border border-dashed border-amber-500/50 bg-amber-500/10 px-1.5 py-0.5 text-xs font-normal text-foreground hover:bg-amber-500/15"
          data-testid={`role-hypothesis-trigger-${targetPosition}`}
          aria-label={t('play.roleHypotheses.triggerLabel', {
            position: targetPosition,
            name: targetName,
          })}
          aria-describedby={[
            descriptionId,
            hiddenGuessCount > 0 ? moreDescriptionId : null,
            statusId,
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={event => event.stopPropagation()}
        >
          <Pencil
            className="size-3 shrink-0 text-amber-600 dark:text-amber-300"
            aria-hidden="true"
          />
          <span
            className="shrink-0 font-medium text-amber-700 dark:text-amber-300"
            data-testid={`role-hypothesis-label-${targetPosition}`}
          >
            {t('play.roleHypotheses.myGuess')}
          </span>
          {displayedGuesses.length > 0 && (
            <span className="text-muted-foreground" aria-hidden="true">
              ·
            </span>
          )}
          {displayedGuesses.map(guess => (
            <span
              key={guess.key}
              className="inline-flex min-w-0 items-center gap-0.5 rounded bg-amber-500/15 px-1 py-0.5"
            >
              <span className="truncate">
                {guess.emoji} {guess.name}
              </span>
            </span>
          ))}
          {hiddenGuessCount > 0 && (
            <>
              <span aria-hidden="true">+{hiddenGuessCount}</span>
              <span id={moreDescriptionId} className="sr-only">
                {t('play.roleHypotheses.selectedMore', {
                  count: hiddenGuessCount,
                })}
              </span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-72 min-w-56 overscroll-contain"
        onEscapeKeyDown={event => event.stopPropagation()}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          triggerRef.current?.focus()
        }}
      >
        <FactionGroup
          selectedFactions={desiredSelection.factions}
          onToggle={toggleFaction}
          t={t}
        />
        <DropdownMenuSeparator />
        <RoleGroup
          label={t('play.roleHypotheses.goodGroup')}
          roles={candidates.good}
          selectedRoles={desiredSelection.roles}
          onToggle={toggleRole}
          t={t}
        />
        <DropdownMenuSeparator />
        <RoleGroup
          label={t('play.roleHypotheses.evilGroup')}
          roles={candidates.evil}
          selectedRoles={desiredSelection.roles}
          onToggle={toggleRole}
          t={t}
        />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={selectedGuesses.length === 0}
          onSelect={(event) => {
            event.preventDefault()
            replaceDesiredSelection({ factions: [], roles: [] })
          }}
        >
          {t('play.roleHypotheses.clear')}
        </DropdownMenuItem>
      </DropdownMenuContent>
      <span id={descriptionId} className="sr-only">
        {selectedGuessNames
          ? t('play.roleHypotheses.selectedSummary', {
              roles: selectedGuessNames,
            })
          : t('play.roleHypotheses.choose')}
      </span>
      <span
        id={statusId}
        role="status"
        aria-live="polite"
        data-testid={`role-hypothesis-status-${targetPosition}`}
        className="sr-only"
      >
        {statusText}
      </span>
    </DropdownMenu>
  )
}

function FactionGroup({
  selectedFactions,
  onToggle,
  t,
}: {
  selectedFactions: RoleHypothesisFaction[]
  onToggle: (faction: RoleHypothesisFaction, checked: boolean) => void
  t: (key: string, params?: Record<string, string | number>) => string
}) {
  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>
        {t('play.roleHypotheses.factionGroup')}
      </DropdownMenuLabel>
      {FACTION_ORDER.map(faction => (
        <DropdownMenuCheckboxItem
          key={faction}
          checked={selectedFactions.includes(faction)}
          onCheckedChange={checked => onToggle(faction, checked === true)}
          onSelect={event => event.preventDefault()}
        >
          <span aria-hidden="true">{FACTION_EMOJIS[faction]}</span>
          <span>{t(FACTION_LABEL_KEYS[faction])}</span>
        </DropdownMenuCheckboxItem>
      ))}
    </DropdownMenuGroup>
  )
}

function RoleGroup({
  label,
  roles,
  selectedRoles,
  onToggle,
  t,
}: {
  label: string
  roles: PlayerRoleType[]
  selectedRoles: PlayerRoleType[]
  onToggle: (role: PlayerRoleType, checked: boolean) => void
  t: (key: string, params?: Record<string, string | number>) => string
}) {
  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>{label}</DropdownMenuLabel>
      {roles.map(role => (
        <DropdownMenuCheckboxItem
          key={role}
          checked={selectedRoles.includes(role)}
          onCheckedChange={checked => onToggle(role, checked === true)}
          onSelect={event => event.preventDefault()}
        >
          <span aria-hidden="true">{ROLE_EMOJIS[role]}</span>
          <span>{t(`game.${role}`)}</span>
        </DropdownMenuCheckboxItem>
      ))}
    </DropdownMenuGroup>
  )
}
